<template>
  <VAvatar
    ref="baseAvatar"
    class="base-avatar"
    :color="placeholderBackgroundColor ?? defaultBackgroundColor"
    :size="size"
  >
    <slot name="default">
      <VImg
        :src="imgSrc"
        :alt="alt"
        cover
      >
        <template #placeholder>
          <div
            v-if="!useInitials"
            class="d-flex align-center justify-center fill-height"
          >
            <VIcon
              :color="iconColor"
              :icon="icon"
              :size="icon === '$twi-avatar' ? size: contentSize"
            />
          </div>
          <div
            v-else
            class="d-flex align-center justify-center fill-height prevent-select-text"
            data-testid="initials"
          >
            <span class="content-size">{{ initials }}</span>
          </div>
        </template>
      </VImg>
    </slot>
  </VAvatar>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(defineProps<{
  /** Alternative text for the avatar image. */
  alt?: string;
  /** Source URL for the avatar image. */
  src: string | null | undefined;
  /** Size of the avatar in pixels. */
  size?: number | string;
  /** Icon to display when no image is provided and useInitials is false. */
  icon?: string;
  /** User's full name for generating initials. */
  userName?: string;
  /** Custom background color for the placeholder. */
  placeholderBackgroundColor?: string;
  /** Color of the icon when displayed. */
  iconColor?: string;
  /** Whether to display user initials instead of an icon when no image is provided. */
  useInitials?: boolean;
}>(), {
  alt: '',
  src: null,
  size: 40,
  icon: '$twi-avatar',
  userName: '',
  iconColor: 'primary',
  useInitials: false,
  placeholderBackgroundColor: undefined,
});

/**
 * Computed property that returns the image source or undefined
 */
const imgSrc = computed(() => props.src ?? undefined);

/**
 * Computed property that determines the background color based on the content type
 */
const defaultBackgroundColor = computed(() => {
  return imgSrc.value || (props.icon && !props.useInitials) ? 'white' : 'neutral-500';
});

/**
 * Computed property that determines the size of the content
 */
const contentSize = computed(() => {
  const sizeInt = typeof props.size === 'string' ? parseInt(props.size) : props.size;
  return Math.round(sizeInt * 0.5);
});

/**
 * Computed property that generates initials from the user's name
 * Takes up to two words and uses their first letters
 */

const initials = computed(() => {
  return props.userName
    .split(' ')
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
});
</script>

<style scoped lang="scss">
.content-size {
  font-size: v-bind('contentSize + "px"') !important;
}
</style>
